import React, { useState, useEffect, useRef } from "react";
import SuperAdminSidebar from "../SuperAdminSidebar";
import ManagerSidebar from "../ManagerSidebar";
import LeaderSidebar from "../LeaderSidebar";
import MemberSidebar from "../MemberSidebar";
import AdminNavbar from './AdminNavbar';
import AllLeads from "./AllLeads";
import NewLeadForm from "./NewLeadForm";
import ImportLeads from "./ImportLeads";
import './Leads.css';

const Leads = () => {
    const [activeTab, setActiveTab] = useState('All Leads');
    const [editLeadId, setEditLeadId] = useState(null);
    const [showEditDrawer, setShowEditDrawer] = useState(false);
    const [drawerMounted, setDrawerMounted] = useState(false);
    const [drawerClosing, setDrawerClosing] = useState(false);

    // ====== FILTER STATE ======
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState(null); // 'source' | 'status' | null
    const filterRef = useRef(null);

    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");
    const userFullName = (localStorage.getItem("fname") || "") + " " + (localStorage.getItem("lname") || "");

    // Options for the fly-out submenus
    const sourceOptions = ['Facebook', 'Instagram', 'Google', 'WhatsApp', 'X (Twitter)', 'Call', 'Email', 'Website', 'Referral'];
    const statusOptions = ['New', 'Contacted', 'Converted', 'Qualified', 'Lost', 'Open', 'Mobile'];

    // ====== HANDLE LEAD CREATED ======
    const handleLeadCreated = () => {
        setActiveTab('All Leads');
    };

    // Trigger the drawer's open animation AFTER it mounts (next frame)
    useEffect(() => {
        if (showEditDrawer && !drawerClosing) {
            const id = requestAnimationFrame(() => {
                requestAnimationFrame(() => setDrawerMounted(true));
            });
            return () => cancelAnimationFrame(id);
        }
    }, [showEditDrawer, drawerClosing]);

    // ====== CLOSE FILTER MENU ON OUTSIDE CLICK ======
    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterMenuOpen(false);
                setOpenSubmenu(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ====== OPEN EDIT DRAWER ======
    const openEditDrawer = (id) => {
        setEditLeadId(id);
        setDrawerClosing(false);
        setDrawerMounted(false);
        setShowEditDrawer(true);
    };

    // ====== CLOSE EDIT DRAWER ======
    const closeEditDrawer = () => {
        if (!showEditDrawer) return;
        setDrawerClosing(true);
        setTimeout(() => {
            setShowEditDrawer(false);
            setEditLeadId(null);
            setDrawerClosing(false);
            setDrawerMounted(false);
        }, 280);
    };

    // ====== FILTER HANDLERS ======
    const applyFilter = (value) => {
        setSelectedFilter(value);
        setFilterMenuOpen(false);
        setOpenSubmenu(null);
    };

    // Human-readable label for the current filter
    const getFilterLabel = () => {
        if (selectedFilter === 'all') return 'All';
        if (selectedFilter === 'me') return 'Assigned to me';
        if (selectedFilter === 'everyone') return 'Assigned To Everyone';
        if (selectedFilter.startsWith('source:')) return `Source: ${selectedFilter.split(':')[1]}`;
        if (selectedFilter.startsWith('status:')) return `Status: ${selectedFilter.split(':')[1]}`;
        return 'All';
    };

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <SuperAdminSidebar />}
                {userType === "leader" && <LeaderSidebar />}
                {userType === "manager" && <ManagerSidebar />}
                {userType === "member" && <MemberSidebar />}
            </div>

            <div className="admin-content">
                <AdminNavbar />

                <div className="main-content">

                    <div className="main-section">
                        <h3 className="section-title">Leads</h3>

                        {/* Tabs + Filter */}
                        <div className="leads-tabs-container">
                            <div className="leads-tabs-left">
                                <button
                                    className={`leads-tab-btn ${activeTab === 'All Leads' ? 'active-tab' : ''}`}
                                    onClick={() => setActiveTab('All Leads')}
                                >
                                    All Leads
                                </button>
                                <button
                                    className={`leads-tab-btn ${activeTab === 'New Leads' ? 'active-tab' : ''}`}
                                    onClick={() => setActiveTab('New Leads')}
                                >
                                    New Leads
                                </button>
                                <button
                                    className="leads-tab-btn import-btn"
                                    onClick={() => setActiveTab('Import Leads')}
                                >
                                    Import Leads
                                </button>
                            </div>

                            {/* Filter Dropdown */}
                            <div className="leads-filter-wrap" ref={filterRef}>
                                <button
                                    type="button"
                                    className="leads-filter-trigger"
                                    onClick={() => {
                                        setFilterMenuOpen(open => !open);
                                        setOpenSubmenu(null);
                                    }}
                                >
                                    <span className="leads-filter-label">Filter by</span>
                                    <span className="leads-filter-value">{getFilterLabel()}</span>
                                    <span className={`leads-filter-caret ${filterMenuOpen ? 'open' : ''}`}>▾</span>
                                </button>

                                {filterMenuOpen && (
                                    <div className="leads-filter-menu">
                                        <button
                                            type="button"
                                            className={`leads-filter-item ${selectedFilter === 'all' ? 'active' : ''}`}
                                            onClick={() => applyFilter('all')}
                                        >
                                            All
                                        </button>
                                        <button
                                            type="button"
                                            className={`leads-filter-item ${selectedFilter === 'me' ? 'active' : ''}`}
                                            onClick={() => applyFilter('me')}
                                        >
                                            Assigned to me
                                        </button>
                                        <button
                                            type="button"
                                            className={`leads-filter-item ${selectedFilter === 'everyone' ? 'active' : ''}`}
                                            onClick={() => applyFilter('everyone')}
                                        >
                                            Assigned To Everyone
                                        </button>

                                        {/* By Source submenu */}
                                        <div
                                            className={`leads-filter-item has-submenu ${selectedFilter.startsWith('source:') ? 'active' : ''}`}
                                            onMouseEnter={() => setOpenSubmenu('source')}
                                            onMouseLeave={() => setOpenSubmenu(null)}
                                        >
                                            <span>By Source</span>
                                            <span className="submenu-arrow">▸</span>

                                            {openSubmenu === 'source' && (
                                                <div className="leads-submenu">
                                                    {sourceOptions.map(opt => (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            className={`leads-filter-item ${selectedFilter === `source:${opt}` ? 'active' : ''}`}
                                                            onClick={() => applyFilter(`source:${opt}`)}
                                                        >
                                                            {opt}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* By Status submenu */}
                                        <div
                                            className={`leads-filter-item has-submenu ${selectedFilter.startsWith('status:') ? 'active' : ''}`}
                                            onMouseEnter={() => setOpenSubmenu('status')}
                                            onMouseLeave={() => setOpenSubmenu(null)}
                                        >
                                            <span>By Status</span>
                                            <span className="submenu-arrow">▸</span>

                                            {openSubmenu === 'status' && (
                                                <div className="leads-submenu">
                                                    {statusOptions.map(opt => (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            className={`leads-filter-item ${selectedFilter === `status:${opt}` ? 'active' : ''}`}
                                                            onClick={() => applyFilter(`status:${opt}`)}
                                                        >
                                                            {opt}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tab Content Area */}
                        <div className="leads-content-area">
                            {activeTab === 'All Leads' && (
                                <AllLeads
                                    openEditDrawer={openEditDrawer}
                                    refreshTrigger={activeTab}
                                    filter={selectedFilter}
                                />
                            )}
                            {activeTab === 'New Leads' && <NewLeadForm onLeadCreated={handleLeadCreated} currentUserFullName={userFullName} />}
                            {activeTab === 'Import Leads' && <ImportLeads />}
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== EDIT SIDE DRAWER (Smooth) ========== */}
            {showEditDrawer && (
                <>
                    <div
                        className={`sale-order-drawer ${
                            drawerClosing ? 'drawer-closing' : (drawerMounted ? 'drawer-open' : '')
                        }`}
                    >
                        <div className="sale-order-drawer-header">
                            <h3>Edit Lead</h3>
                            <button
                                onClick={closeEditDrawer}
                                className="sale-order-drawer-close"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <NewLeadForm
                            mode="edit"
                            leadId={editLeadId}
                            onLeadCreated={() => {
                                closeEditDrawer();
                                setActiveTab('All Leads');
                            }}
                            currentUserFullName={userFullName}
                            onCancel={closeEditDrawer}
                        />
                    </div>

                    <div
                        className={`sale-order-drawer-overlay ${
                            drawerClosing ? 'overlay-closing' : (drawerMounted ? 'overlay-open' : '')
                        }`}
                        onClick={closeEditDrawer}
                    />
                </>
            )}
        </div>
    )
}

export default Leads;